import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { useCallback, useState } from 'react';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import { useInvoiceNoteItemDetail } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItms.hooks';
import { useTaskMutation } from '@/core/domain/operational/Tasks/Tasks.hooks';
import { Task } from '@/core/domain/operational/Tasks/Tasks.types';
import { InvoiceServiceFormValues } from './InvoiceServiceForm.types';
import { useCustomerGeneralMutation } from '@/core/domain/people/Customers/Customers.hooks';
import { CustomerGeneral } from '@/core/domain/people/Customers/Customers.types';
import { useInsurerMutation } from '@/core/domain/people/Insurers/Insurers.hooks';
import { Insurer } from '@/core/domain/people/Insurers/Insurers.types';
import moment from 'moment';

interface Props {
  companyId?: number;
}

interface Params {
  taskId?: number;
  customerId?: number;
  insurierId?: number;
  invoiceType?: InvoiceType;
  formValues?: Partial<InvoiceServiceFormValues>;
}

type HookReturn = [
  (params: Params) => Promise<Partial<InvoiceServiceFormValues | undefined>>,
  boolean,
];

export function useInvoiceServiceFillFields(props: Props): HookReturn {
  const taskDetail = useTaskMutation();
  const customerDetail = useCustomerGeneralMutation();
  const insurierDetail = useInsurerMutation();
  const invoiceSettingsData = useInvoiceSettingsByCompany(props.companyId);

  const [loading, setLoading] = useState(false);

  const fetchFields = useCallback(
    async (params: Params): Promise<InvoiceServiceFormValues> => {
      try {
        setLoading(true);

        const formValues = params.formValues;

        const invoiceSettings = invoiceSettingsData.data?.configNfs;

        let task = {} as Task | undefined;

        if (params.taskId) {
          task = await taskDetail.mutateAsync(params.taskId);
        }

        let customer = {} as CustomerGeneral | undefined;
        let insurer = {} as Insurer | undefined;

        if (params.customerId) {
          customer = await customerDetail.mutateAsync(params.customerId);
        }

        if (params.insurierId) {
          insurer = await insurierDetail.mutateAsync(params.insurierId);
        }

        const fillInFieldsIrpjCsll =
          customer?.retentionIRPJorCSLL || insurer?.retentionIRPJorCSLL;

        const inssWithheld =
          String(
            invoiceSettings?.withheldDefault ??
              customer?.retentionISS ??
              insurer?.retentionIss ??
              '',
          ) || undefined;

        const generalValueUnitary = task?.taskPriceSet.find(
          (price) => price.companyId === props.companyId,
        )?.price;

        return {
          general: {
            ...formValues?.general,
            description: task?.description || '',
            quantity: task?.time || 0,
            valueUnitary: generalValueUnitary || 0,
            serviceItemList: invoiceSettings?.issqnServiceCodeDefault,
            unitMeasurement:
              task?.unit || invoiceSettings?.unitMeasurementDefault,
          },

          itemTaxes: {
            dedution: formValues?.itemTaxes?.dedution,
            nbsCode: task?.nbsCode || invoiceSettings?.nbsCodeDefault,

            iss: {
              ...formValues?.itemTaxes?.iss,
              rate: invoiceSettings?.rateDefault,
              taxType: invoiceSettings?.taxTypeDefault,
              exigibility: invoiceSettings?.exigibilityDefault,
              withheld: inssWithheld,
            },

            ibtp: {
              ...formValues?.itemTaxes?.ibtp,
              generalIbptRate:
                invoiceSettings?.generalIbptRate ||
                formValues?.itemTaxes?.ibtp?.generalIbptRate,
              stateIbptRate:
                invoiceSettings?.stateIbptRate ||
                formValues?.itemTaxes?.ibtp?.stateIbptRate,
              municipalIbptRate:
                invoiceSettings?.municipalIbptRate ||
                formValues?.itemTaxes?.ibtp?.municipalIbptRate,
              federalIbptRate:
                invoiceSettings?.federalIbptRate ||
                formValues?.itemTaxes?.ibtp?.federalIbptRate,
            },

            retention: {
              csllWithheldRate: fillInFieldsIrpjCsll
                ? invoiceSettings?.csllAliquotDefault
                : undefined,
              irrfWithheldRate: fillInFieldsIrpjCsll
                ? invoiceSettings?.irrfAliquotDefault
                : undefined,
              ...formValues?.itemTaxes?.retention,
            },

            is: {
              ...formValues?.itemTaxes?.is,
              selectiveTaxAliquot: invoiceSettings?.selectiveTaxAliquotDefault,
              selectiveTaxCst: invoiceSettings?.selectiveTaxCstDefault,
              selectiveTaxClassificationCode:
                invoiceSettings?.selectiveTaxClassificationCodeDefault,
            },

            ibsCbs: {
              ...formValues?.itemTaxes?.ibsCbs,
              ibsCbsCst: invoiceSettings?.ibsCbsCstDefault,
              ibsCbsTaxClassification:
                invoiceSettings?.ibsCbsTaxClassificationDefault,
              ibsUfAliquot: invoiceSettings?.ibsUfAliquotDefault,
              ibsMunicipalAliquot: invoiceSettings?.ibsMunicipalAliquotDefault,
              cbsAliquot: invoiceSettings?.cbsAliquot,
            },
          },
        };
      } finally {
        setLoading(false);
      }
    },
    [invoiceSettingsData.data, props.companyId],
  );

  return [
    fetchFields,
    taskDetail.isLoading ||
      customerDetail.isLoading ||
      invoiceSettingsData.isLoading ||
      loading,
  ];
}

export function useInvoiceServiceDetailFillFieds(): [
  (invoiceItemId?: number) => Promise<InvoiceServiceFormValues>,
  boolean,
] {
  const invoiceNoteItemDetail = useInvoiceNoteItemDetail();

  const fetchFields = useCallback(
    async (invoiceItemId?: number): Promise<InvoiceServiceFormValues> => {
      try {
        const data = await invoiceNoteItemDetail.mutateAsync(invoiceItemId);

        return {
          general: {
            taksId: data.taskId,
            description: data.description,
            unitMeasurement: data.unitMeasurement,
            quantity: data.quantity,
            valueUnitary: data.valueUnitary,
            conditionalDiscount: data.conditionalDiscount,
            unconditionalDiscount: data.unconditionalDiscount,
            grossServiceValue: data.grossServiceValue,
            taxBase: data.taxBase,
            deductions: data.deductions,
            serviceItemList: data.serviceItemList,
          },
          itemTaxes: {
            nbsCode: data.itemTaxes?.nbsCode,
            federalTaxesWithheld: data.itemTaxes?.federalTaxesWithheld
              ? String(data.itemTaxes?.federalTaxesWithheld)
              : undefined,
            withholdingResponsible: data.itemTaxes?.withholdingResponsible,
            dedution: {
              deductionType: data.itemTaxes?.deductionType,
              deductionDescription: data.itemTaxes?.deductionDescription,
            },
            ibtp: {
              generalIbptRate: data.itemTaxes?.generalIbptRate,
              municipalIbptRate: data.itemTaxes?.municipalIbptRate,
              stateIbptRate: data.itemTaxes?.stateIbptRate,
              federalIbptRate: data.itemTaxes?.federalIbptRate,
              generalIbptValue: data.itemTaxes?.generalIbptValue,
              municipalIbptValue: data.itemTaxes?.municipalIbptValue,
              stateIbptValue: data.itemTaxes?.stateIbptValue,
              federalIbptValue: data.itemTaxes?.federalIbptValue,
            },
            iss: {
              taxType: data.itemTaxes?.taxType,
              exigibility: data.itemTaxes?.exigibility,
              withheld:
                data.itemTaxes?.withheld !== undefined
                  ? String(data.itemTaxes.withheld)
                  : undefined,
              rate: data.itemTaxes?.rate,
              value: data.itemTaxes?.value,
              withheldValue: data.itemTaxes?.withheldValue,
              suspensionProcess: data.itemTaxes?.suspensionProcess,
            },
            retention: {
              pisWithheldRate: data.itemTaxes?.pisWithheldRate,
              pisWithheldValue: data.itemTaxes?.pisWithheldValue,
              pisWithheldBase: data.itemTaxes?.pisWithheldBase,
              pisWithheldCst: data.itemTaxes?.pisWithheldCst,
              cofinsWithheldRate: data.itemTaxes?.cofinsWithheldRate,
              cofinsWithheldValue: data.itemTaxes?.cofinsWithheldValue,
              cofinsWithheldBase: data.itemTaxes?.cofinsWithheldBase,
              cofinsWithheldCst: data.itemTaxes?.cofinsWithheldCst,
              csllWithheldRate: data.itemTaxes?.csllWithheldRate,
              csllWithheldValue: data.itemTaxes?.csllWithheldValue,
              inssWithheldRate: data.itemTaxes?.inssWithheldRate,
              inssWithheldValue: data.itemTaxes?.inssWithheldValue,
              irrfWithheldRate: data.itemTaxes?.irrfWithheldRate,
              irrfWithheldValue: data.itemTaxes?.irrfWithheldValue,
              cppWithheldRate: data.itemTaxes?.cppWithheldRate,
              cppWithheldValue: data.itemTaxes?.cppWithheldValue,
              otherWithholdings: data.itemTaxes?.otherWithholdings,
              pisCofinsOwnCst: data.itemTaxes?.pisCofinsOwnCst,
              pisCofinsOwnBase: data.itemTaxes?.pisCofinsOwnBase,
              cofinsOwnRate: data.itemTaxes?.cofinsOwnRate,
              cofinsOwnValue: data.itemTaxes?.cofinsOwnValue,
              pisOwnRate: data.itemTaxes?.pisOwnRate,
              pisOwnValue: data.itemTaxes?.pisOwnValue,
            },
            is: {
              selectiveTaxCst: data.itemTaxes?.selectiveTaxCst,
              selectiveTaxClassificationCode:
                data.itemTaxes?.selectiveTaxClassificationCode,
              selectiveTaxBaseCalculationValue:
                data.itemTaxes?.selectiveTaxBaseCalculationValue,
              selectiveTaxAliquot: data.itemTaxes?.selectiveTaxAliquot,
              selectiveTaxSpecific: data.itemTaxes?.selectiveTaxSpecific,
              selectiveTaxTaxableUnitMeasure:
                data.itemTaxes?.selectiveTaxTaxableUnitMeasure,
              selectiveTaxTaxableQuantity:
                data.itemTaxes?.selectiveTaxTaxableQuantity,
              selectiveTaxValue: data.itemTaxes?.selectiveTaxValue,
            },
            ibsCbs: {
              ibsCbsCst: data.itemTaxes?.ibsCbsCst,
              ibsCbsTaxClassification: data.itemTaxes?.ibsCbsTaxClassification,
              ibsCbsDonation: data.itemTaxes?.ibsCbsDonation,
              ibsCbsBaseCalculation: data.itemTaxes?.ibsCbsBaseCalculation,
              ibsUfAliquot: data.itemTaxes?.ibsUfAliquot,
              ibsUfReductionAliquot: data.itemTaxes?.ibsUfReductionAliquot,
              ibsUfEffectiveAliquot: data.itemTaxes?.ibsUfEffectiveAliquot,
              ibsUfDefermentPercentage:
                data.itemTaxes?.ibsUfDefermentPercentage,
              ibsUfDefermentValue: data.itemTaxes?.ibsUfDefermentValue,
              ibsUfTaxReturned: data.itemTaxes?.ibsUfTaxReturned,
              ibsUfValue: data.itemTaxes?.ibsUfValue,
              ibsMunicipalAliquot: data.itemTaxes?.ibsMunicipalAliquot,
              ibsMunicipalReductionAliquot:
                data.itemTaxes?.ibsMunicipalReductionAliquot,
              ibsMunicipalEffectiveAliquot:
                data.itemTaxes?.ibsMunicipalEffectiveAliquot,
              ibsMunicipalDeferralPercentage:
                data.itemTaxes?.ibsMunicipalDeferralPercentage,
              ibsMunicipalDeferralValue:
                data.itemTaxes?.ibsMunicipalDeferralValue,
              ibsMunicipalTaxReturned: data.itemTaxes?.ibsMunicipalTaxReturned,
              ibsMunicipalValue: data.itemTaxes?.ibsMunicipalValue,
              ibsTotalValue: data.itemTaxes?.ibsTotalValue,
              cbsAliquot: data.itemTaxes?.cbsAliquot,
              cbsReductionAliquot: data.itemTaxes?.cbsReductionAliquot,
              cbsEffectiveAliquot: data.itemTaxes?.cbsEffectiveAliquot,
              cbsDeferralPercentage: data.itemTaxes?.cbsDeferralPercentage,
              cbsDeferralValue: data.itemTaxes?.cbsDeferralValue,
              cbsTaxReturned: data.itemTaxes?.cbsTaxReturned,
              cbsValue: data.itemTaxes?.cbsValue,
              // Regular taxation ---
              regularTaxIbsCbsCst: data.itemTaxes?.regularTaxIbsCbsCst,
              regularTaxIbsCbsClassificationCode:
                data.itemTaxes?.regularTaxIbsCbsClassificationCode,
              regularTaxIbsUfAliquotPercentage:
                data.itemTaxes?.regularTaxIbsUfAliquotPercentage,
              regularTaxIbsUfValue: data.itemTaxes?.regularTaxIbsUfValue,
              regularTaxIbsMunicipalAliquotPercentage:
                data.itemTaxes?.regularTaxIbsMunicipalAliquotPercentage,
              regularTaxIbsMunicipalValue:
                data.itemTaxes?.regularTaxIbsMunicipalValue,
              regularTaxCbsAliquotPercentage:
                data.itemTaxes?.regularTaxCbsAliquotPercentage,
              regularTaxCbsValue: data.itemTaxes?.regularTaxCbsValue,
              // GovernmentProcurement ---
              govPurchaseIbsUfAliquotPercentage:
                data.itemTaxes?.govPurchaseIbsUfAliquotPercentage,
              govPurchaseIbsUfValue: data.itemTaxes?.govPurchaseIbsUfValue,
              govPurchaseIbsMunicipalAliquotPercentage:
                data.itemTaxes?.govPurchaseIbsMunicipalAliquotPercentage,
              govPurchaseIbsMunicipalValue:
                data.itemTaxes?.govPurchaseIbsMunicipalValue,
              govPurchaseCbsAliquotPercentage:
                data.itemTaxes?.govPurchaseCbsAliquotPercentage,
              govPurchaseCbsValue: data.itemTaxes?.govPurchaseCbsValue,
              // Single Phase ---
              monoQuantity: data.itemTaxes?.monoQuantity,
              monoAdRemIbsAliquotPercentage:
                data.itemTaxes?.monoAdRemIbsAliquotPercentage,
              monoAdRemCbsAliquotPercentage:
                data.itemTaxes?.monoAdRemCbsAliquotPercentage,
              monoIbsValue: data.itemTaxes?.monoIbsValue,
              monoCbsValue: data.itemTaxes?.monoCbsValue,
              monoRetentionQuantity: data.itemTaxes?.monoRetentionQuantity,
              monoRetentionAdRemIbsAliquotPercentage:
                data.itemTaxes?.monoRetentionAdRemIbsAliquotPercentage,
              monoRetentionIbsValue: data.itemTaxes?.monoRetentionIbsValue,
              monoRetentionAdRemCbsAliquotPercentage:
                data.itemTaxes?.monoRetentionAdRemCbsAliquotPercentage,
              monoRetentionCbsValue: data.itemTaxes?.monoRetentionCbsValue,
              monoRetainedQuantity: data.itemTaxes?.monoRetainedQuantity,
              monoRetainedAdRemIbsAliquotPercentage:
                data.itemTaxes?.monoRetainedAdRemIbsAliquotPercentage,
              monoRetainedIbsValue: data.itemTaxes?.monoRetainedIbsValue,
              monoRetainedAdRemCbsAliquotPercentage:
                data.itemTaxes?.monoRetainedAdRemCbsAliquotPercentage,
              monoRetainedCbsValue: data.itemTaxes?.monoRetainedCbsValue,
              monoDeferralIbsPercentage:
                data.itemTaxes?.monoDeferralIbsPercentage,
              monoDeferralIbsValue: data.itemTaxes?.monoDeferralIbsValue,
              monoDeferralCbsPercentage:
                data.itemTaxes?.monoDeferralCbsPercentage,
              monoDeferralCbsValue: data.itemTaxes?.monoDeferralCbsValue,
              monoItemIbsTotal: data.itemTaxes?.monoItemIbsTotal,
              monoItemCbsTotal: data.itemTaxes?.monoItemCbsTotal,
              // Competency Adjustment ---
              competenceApuration: data.itemTaxes?.competenceApuration
                ? moment(data.itemTaxes?.competenceApuration)
                : undefined,
              competenceIbsValue: data.itemTaxes?.competenceIbsValue,
              competenceCbsValue: data.itemTaxes?.competenceCbsValue,
              // Credit Transfers/Reversal ---
              creditTransferIbsValue: data.itemTaxes?.creditTransferIbsValue,
              creditTransferCbsValue: data.itemTaxes?.creditTransferCbsValue,
              creditReversalIbsValue: data.itemTaxes?.creditReversalIbsValue,
              creditReversalCbsValue: data.itemTaxes?.creditReversalCbsValue,
              // Presumed Credit ---
              classificationCodePresumedCredit:
                data.itemTaxes?.classificationCodePresumedCredit,
              ibsPresumedCreditPercentage:
                data.itemTaxes?.ibsPresumedCreditPercentage,
              ibsPresumedCreditValue: data.itemTaxes?.ibsPresumedCreditValue,
              ibsPresumedCreditSuspensiveConditionValue:
                data.itemTaxes?.ibsPresumedCreditSuspensiveConditionValue,
              cbsPresumedCreditPercentage:
                data.itemTaxes?.cbsPresumedCreditPercentage,
              cbsPresumedCreditValue: data.itemTaxes?.cbsPresumedCreditValue,
              cbsPresumedCreditSuspensiveConditionValue:
                data.itemTaxes?.cbsPresumedCreditSuspensiveConditionValue,
              // Presumed Credit IBS ZFM ---
              ibsZfmAssessmentPeriod: data.itemTaxes?.ibsZfmAssessmentPeriod
                ? moment(data.itemTaxes?.ibsZfmAssessmentPeriod, 'YYYY-MM')
                : undefined,
              ibsZfmPresumedCreditType:
                data.itemTaxes?.ibsZfmPresumedCreditType,
              ibsZfmPresumedCreditValue:
                data.itemTaxes?.ibsZfmPresumedCreditValue,
            },
          },
        };
      } finally {
      }
    },
    [],
  );

  return [fetchFields, invoiceNoteItemDetail.isLoading];
}
